import { TypeformForm } from '@/api/model/typeform/typeform-form'
import { TypeformLogicJumpFlattenedCondition } from '@/api/model/typeform/typeform-logic-jump-flattened-condition'
import { TypeformMap } from '@/api/model/typeform/typeform-map'
import { TypeformReferenceRegExp } from '@/api/model/typeform/typeform-form'

export class TypeformThankYouScreen {
    constructor(
        public id: string,
        public ref: string,
        public title: string,
        public _type: string|null,
        public properties: TypeformMap,
        public attachment: TypeformMap|null,
        public layout: TypeformMap|null,
    ) {
        this.initProperties(this.properties)
    }

    get description(): string {
        return this.properties.description as string
    }

    set description(description: string) {
        this.properties.description = description
    }

    get type(): string {
        return this._type || 'thankyou_screen'
    }

    set type(type: string|null) {
        this._type = type
        this.initProperties()
    }

    initProperties(properties: TypeformMap = {}): void {
        if (this.isDefault) {
            this.properties = properties
            return
        }

        const propertyDefinitions = {
            description: { types: ['thankyou_screen'], default: '' },
            show_button: { types: ['thankyou_screen'], default: false },
            button_text: { types: ['thankyou_screen'], default: '' },
            button_mode: { types: ['thankyou_screen'], default: 'default_redirect' },
            redirect_url: { types: ['thankyou_screen', 'url_redirect'], default: '' },
            share_icons: { types: ['thankyou_screen'], default: false },
        }

        for (const property of Object.keys(propertyDefinitions)) {
            const propertyDefinition = propertyDefinitions[property]

            const types = 'types' in propertyDefinition ? propertyDefinition.types : []
            if (types.length > 0 && !types.includes(this.type)) {
                continue
            }

            const currentValue = property in this.properties ? this.properties[property] : null
            const defaultValue = 'default' in propertyDefinition ? propertyDefinition.default : null

            properties[property] = currentValue ?? defaultValue
        }

        this.properties = properties
    }

    getAllowedReferenceOptions(form: TypeformForm, valueCallback: (type: string, value: string) => string, allowSelf: boolean = false) {
        const options = []

        // All hidden fields
        form.hidden.forEach(hidden => {
            options.push({
                label: hidden,
                value: valueCallback('hidden', hidden),
            })
        })

        // All fields that can be used in conditions (ie. can have an answer)
        form.fields.forEach(field => {
            const ops = TypeformLogicJumpFlattenedCondition.getFieldOps(field)
            if (ops.length === 0) {
                return
            }

            options.push({
                label: field.title.replaceAll(TypeformReferenceRegExp, '_'),
                value: valueCallback('field', field.ref),
            })
        })

        return options
    }

    getAllowedReferences(form: TypeformForm, valueCallback: (type: string, value: string) => string, allowSelf: boolean = false) {
        return this.getAllowedReferenceOptions(form, valueCallback, allowSelf).map(allowedReference => allowedReference.value)
    }

    get isDefault(): boolean {
        return this.id === 'DefaultTyScreen'
    }

    toRequestData(): TypeformMap {
        const requestData: TypeformMap = {
            id: this.id,
            ref: this.ref,
            title: this.title,
            type: this.type,
        }

        const properties = this.properties
        if (properties.button_mode && properties.button_mode !== 'redirect') {
            delete properties.redirect_url
        }

        requestData.properties = properties

        if (this.attachment) {
            requestData.attachment = this.attachment
        }

        if (this.layout) {
            requestData.layout = this.layout
        }

        return requestData
    }

    static fromData(data: any): TypeformThankYouScreen {
        if (data === null || typeof data === 'string' || data instanceof TypeformThankYouScreen) {
            return data
        }

        return new TypeformThankYouScreen(
            data.id,
            data.ref,
            data.title || '',
            data.type,
            data.properties || {},
            data.attachment,
            data.layout,
        )
    }
}
